// src/services/terminalChat.js
// Reusable streaming helper for Forge (/coding) and Stratos (/grok) terminals.
// Mirrors the exact SSE protocol from sendChatQueryStreaming in chat.js.

/**
 * @param {string} endpoint  - e.g. "/coding/stream" or "/grok/stream"
 * @param {string} message
 * @param {Array}  history
 * @param {Object} callbacks - { onStatus, onChunk, onDone, onError }
 * @param {Object} extras    - additional fields merged into request body (e.g. { mode: 'general' })
 */
export const sendTerminalStreaming = async (endpoint, message, history = [], callbacks = {}, extras = {}) => {
  const { onStatus, onChunk, onDone, onError } = callbacks;

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const token = localStorage.getItem('token');

  // Phase 1 auth: cookies carry the session (credentials: 'include'),
  // X-CSRF-Token is injected automatically by the global fetch interceptor
  // in index.js, and the legacy X-User header is gone.
  const response = await fetch(`${baseURL}${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ message, history, ...extras }),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(body || `HTTP ${response.status}: ${response.statusText}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop(); // keep incomplete line

    let eventType = null;
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        const data = line.slice(6);
        switch (eventType) {
          case 'status':
            onStatus?.(data);
            break;
          case 'chunk':
            try { onChunk?.(JSON.parse(data)); }
            catch { onChunk?.(data); }
            break;
          case 'done':
            try { onDone?.(JSON.parse(data)); }
            catch { onDone?.({ content: data }); }
            break;
          case 'error':
            try { onError?.(JSON.parse(data)); }
            catch { onError?.({ error: data }); }
            break;
          default:
            break;
        }
        eventType = null;
      }
    }
  }
};
